import clsx from 'clsx';
import {useQueryClient} from 'react-query';
import {useEffect, useRef, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {useAuth} from '../../../modules/auth';
import {useI18n} from '../../providers/I18nProvider';
import {PageDataProvider} from '../../providers/PageDataProvider';
import Backdrop from '../Backdrop';
import ConfirmModal from '../modals/ConfirmModal';
import Breadcrumb from './breadcrumb/Breadcrumb';
import Footer from './footer/Footer';
import {Header} from './header/Header';

import {useLayout} from './LayoutProvider';
import {ScrollTop} from './ScrollTop';
import Sidebar from './sidebar/Sidebar';

// import useWindowDimensions from '../../hooks/useWindowDimensions';

const MasterLayout = () => {
	const {isSidebarShown, toggleSidebar, setHeaderHeight, hasFixedContentUnderHeader} = useLayout();
	const {logout} = useAuth();
	const {i18n} = useI18n();
	const {pathname} = useLocation();
	const isCampaign = pathname === '/campaign/';

	const [showLogoutModal, setShowLogoutModal] = useState(false);

	const headerRef = useRef<HTMLElement>(null);
	const queryClient = useQueryClient();

	useEffect(() => {
		if (headerRef.current) setHeaderHeight(headerRef.current.clientHeight);
	}, [setHeaderHeight]);

	return (
		<PageDataProvider>
			<div className='flex'>
				<aside
					className={clsx(
						{'-translate-x-full': !isSidebarShown},
						'sm:flex sm:w-[30vw] xl:w-[25vw] 2xl:w-[20vw] fixed left-0 top-0 z-30 h-screen bg-white duration-500 ease-in-out transition-transform sm:translate-x-0 overflow-y-scroll border-r',
					)}
				>
					<Sidebar setShowLogoutModal={setShowLogoutModal} />
				</aside>

				<div className='w-full sm:ml-[30vw] xl:ml-[25vw] 2xl:ml-[20vw] sm:w-[70vw] xl:w-[75vw] 2xl:w-[80vw]'>
					{isSidebarShown && <Backdrop onClick={toggleSidebar} />}

					<header
						className={clsx('sticky top-0 z-[10] w-full bg-white', {
							'border-b border-black/10': !hasFixedContentUnderHeader,
						})}
						ref={headerRef}
					>
						<Header setShowLogoutModal={setShowLogoutModal} />
					</header>

					<main
						className={clsx('bg-body sm:min-h-[90vh] lg:min-h-[85vh] xl:p-6', {
							'p-0': hasFixedContentUnderHeader,
							'p-4': !hasFixedContentUnderHeader && !isCampaign,
						})}
					>
						<Breadcrumb />
						<Outlet />
					</main>
					<footer className='hidden sm:block w-full bg-white border-t border-black/10 '>
						<Footer />
					</footer>
				</div>
			</div>

			<ScrollTop />

			<ConfirmModal
				show={showLogoutModal}
				onChoice={(choice) => {
					if (choice) logout();
					setShowLogoutModal(false);
					queryClient.removeQueries();
				}}
				message={i18n('LAYOUT.MODAL.LOGOUT.BODY')}
				title={i18n('LAYOUT.MODAL.LOGOUT.HEADER')}
				cancelText={i18n('COMMON.NOT_NOW')}
			/>
		</PageDataProvider>
	);
};

export {MasterLayout};
